import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadStatementTable from "@tggstore/Store/Handlers/Custom/LoadStatementTable";
import { CustomState } from "@tggstore/Store/Reducers";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomState) => ({
    statements: state.customStatement.statements || [],
    isLoading: state.customStatement?.isLoading || false,
});

const mapDispatchToProps = {
    loadStatementTable,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StatementTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    periodHeader?: DataTableHeaderProps;
    balanceHeader?: DataTableHeaderProps;
    periodCell?: DataTableCellBaseProps;
    balanceCell?: DataTableCellBaseProps;
}

export const statementTableStyles: StatementTableStyles = {
    container: {
        css: css`
            overflow: auto;
            margin-top: 5px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    dataTable: {
        css: css`
            border: 1px solid #e0e0e0;
            border-collapse: separate;
            border-spacing: 0;
        `,
    },
    dataTableHead: {
        css: css`
            background-color: #f5f5f5;
        `,
    },
    periodHeader: {
        css: css`
            border-right: 1px solid #e0e0e0;
            font-weight: bold;
        `,
    },
    balanceHeader: {
        alignX: "right",
        css: css`
            font-weight: bold;
        `,
    },
    periodCell: {
        css: css`
            border-right: 1px solid #e0e0e0;
        `,
    },
    balanceCell: {
        alignX: "right",
        css: css`
            border-right: 1px solid #e0e0e0;
        `,
    },
};

const styles = statementTableStyles;

const StatementTable: FC<Props> = ({ statements, isLoading, loadStatementTable }) => {
    useEffect(() => {
        loadStatementTable({});
    }, []);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!statements || (Array.isArray(statements) && statements.length === 0)) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{siteMessage("Statement_NoData")}</Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader tight {...styles.periodHeader}>
                        {translate("Period")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.balanceHeader}>
                        {translate("Balance")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {(statements as Array<{ id: string; label: string; balance: string }>).map(
                        ({ id, label, balance }) => (
                            <DataTableRow key={id} {...styles.dataTableRow}>
                                <DataTableCell {...styles.periodCell}>{label}</DataTableCell>
                                <DataTableCell {...styles.balanceCell}>{balance}</DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(StatementTable),
    definition: {
        group: "My Account",
        displayName: "Statement Table",
    },
};

export default widgetModule;
